import { create } from 'zustand';
import * as types from '../types';
import axios from 'axios';

// Dynamically resolve API URL based on current hostname so the site works
// from any device on the LAN without rebuilding.
const API_URL = (() => {
  if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
    return process.env.REACT_APP_API_URL;
  }
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1'
    ? 'http://localhost:5001/api'
    : `http://${host}:5001/api`;
})();

// ==========================================
// AUTH STORE
// ==========================================
interface AuthState {
  user: types.User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;

  setUser: (user: types.User | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  isLoading: false,
  error: null,

  setUser: (user) => set({ user }),
  setToken: (token) => set({ token }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });

      const { user, token } = response.data.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      set({ user, token, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    axios.defaults.headers.common['Authorization'] = '';
    set({ user: null, token: null, error: null });
  },
}));

// Initialize from localStorage — wipe corrupt values from failed logins
try {
  const savedToken = localStorage.getItem('token');
  const savedUser = localStorage.getItem('user');
  if (savedToken && savedToken !== 'undefined' && savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
    const parsedUser = JSON.parse(savedUser);
    if (parsedUser && parsedUser.id) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      useAuthStore.setState({ token: savedToken, user: parsedUser });
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
} catch {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
}

export const useAuth = useAuthStore;

// ==========================================
// BRANCH STORE
// ==========================================
interface BranchState {
  branches: types.Branch[];
  selectedBranch: types.Branch | null;
  isLoading: boolean;
  error: string | null;

  setBranches: (branches: types.Branch[]) => void;
  setSelectedBranch: (branch: types.Branch | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useBranchStore = create<BranchState>((set) => ({
  branches: [],
  selectedBranch: null,
  isLoading: false,
  error: null,

  setBranches: (branches) => set({ branches }),
  setSelectedBranch: (branch) => set({ selectedBranch: branch }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
}));

// ==========================================
// PRODUCT STORE
// ==========================================
interface ProductState {
  products: types.Product[];
  isLoading: boolean;
  error: string | null;

  setProducts: (products: types.Product[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useProductStore = create<ProductState>((set) => ({
  products: [],
  isLoading: false,
  error: null,

  setProducts: (products) => set({ products }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
}));

// ==========================================
// TRANSACTION STORE
// ==========================================
interface TransactionState {
  transactions: types.Transaction[];
  selectedTransaction: types.Transaction | null;
  isLoading: boolean;
  error: string | null;

  setTransactions: (transactions: types.Transaction[]) => void;
  setSelectedTransaction: (transaction: types.Transaction | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useTransactionStore = create<TransactionState>((set) => ({
  transactions: [],
  selectedTransaction: null,
  isLoading: false,
  error: null,

  setTransactions: (transactions) => set({ transactions }),
  setSelectedTransaction: (transaction) => set({ selectedTransaction: transaction }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
}));

// ==========================================
// BOOKING STORE
// ==========================================
interface BookingState {
  bookings: types.Booking[];
  selectedBooking: types.Booking | null;
  isLoading: boolean;
  error: string | null;

  setBookings: (bookings: types.Booking[]) => void;
  setSelectedBooking: (booking: types.Booking | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useBookingStore = create<BookingState>((set) => ({
  bookings: [],
  selectedBooking: null,
  isLoading: false,
  error: null,

  setBookings: (bookings) => set({ bookings }),
  setSelectedBooking: (booking) => set({ selectedBooking: booking }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
}));

// ==========================================
// DASHBOARD STORE
// ==========================================
interface DashboardState {
  stats: types.DashboardStats | null;
  isLoading: boolean;
  error: string | null;

  setStats: (stats: types.DashboardStats | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useDashboardStore = create<DashboardState>((set) => ({
  stats: null,
  isLoading: false,
  error: null,

  setStats: (stats) => set({ stats }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
}));
